import { Button, Form, Input, Row, Select, Space, Table, Tooltip, Upload } from 'antd';
import { UploadOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import {
  convertCsvToMatrix,
  convertMatrixToCsvString,
  downloadCsvFile,
  readFileAsString,
} from '../../../store/files/fileUtils';
import { useState } from 'react';
import { BooleanShipiumTag } from '../../../components/shared/ShipiumTag';
import {
  extractZipZoneDataFromMatrix,
  generateZipZonePreviewTableColumns,
  generateZipZoneTemplateMatrix,
} from './util';
import countries from 'i18n-iso-countries';
import { format } from 'date-fns';
import type { CarrierCarrierInfo } from '@shipium-corp/shipium-ts-types';

export enum ZipZoneAction {
  ADD = 'Add',
  DELETE = 'Delete',
  REPLACE = 'Replace',
}

export interface ZipZoneType {
  displayName: string;
  carrierEnum: string;
}

export const ZipZoneTypes = {
  COUNTRY_TO_COUNTRY: { displayName: 'Country to Country', carrierEnum: 'any_country_to_any_country' } as ZipZoneType,
  COUNTRY_TO_POSTAL5: { displayName: 'Country to Type 5 Postal', carrierEnum: 'any_country_to_postal' } as ZipZoneType,
  TYPE3_TO_TYPE5: { displayName: 'Type 3 Postal to Type 5 Postal', carrierEnum: 'postal3_to_postal' } as ZipZoneType,
  TYPE5_TO_TYPE5: { displayName: 'Type 5 Postal to Type 5 Postal', carrierEnum: 'postal_to_postal' } as ZipZoneType,
  // postal3_to_postal1,
  // state_group_to_any_country,
  // postal3_to_any_country,
  // any_US_origin_to_postal_group,
  // any_US_origin_to_service_method_postal_group,
  // state_group_to_postal_group,
  // unknown
};

export interface ZipZoneRecord {
  fromPostalCode?: string;
  fromCountryCode: string;
  toPostalCode?: string;
  toCountryCode: string;
  zone: string;
}

export interface ZipZoneUploadData {
  type: ZipZoneType;
  csvString: string;
  action: ZipZoneAction;
  carrier: CarrierCarrierInfo;
  originCountryCode: string;
}

export interface ZipZoneUploadProps {
  setZipZones: (data: ZipZoneUploadData) => void;
  carriers: CarrierCarrierInfo[];
  defaultCarrierId?: string;
  defaultZoneKeyType?: string;
}

const zonesForCarrier = (carrier: CarrierCarrierInfo) => {
  const allZones = carrier.serviceMethods.flatMap((s) => Object.values(s.carrierZoneKeyTypeByCountry ?? {}));
  const supportedZones = allZones.map((keyType) => Object.values(ZipZoneTypes).find((z) => z.carrierEnum === keyType));
  const filteredZones = supportedZones.filter((z) => !!z).map((z) => z as ZipZoneType);
  const uniqueZones = filteredZones.filter((z, i, arr) => {
    return i === arr.indexOf(z);
  });
  const sortedZones = uniqueZones.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return sortedZones;
};
const DEFAULT_ROWS_TO_SHOW = 10;
const ZipZoneUpload = (props: ZipZoneUploadProps) => {
  const [form] = Form.useForm();
  const [searchCriteria, setSearchCriteria] = useState<string>('');
  const [action, setAction] = useState<ZipZoneAction>(ZipZoneAction.ADD);
  const [carrier, setCarrier] = useState<CarrierCarrierInfo>(
    props.defaultCarrierId
      ? (props.carriers.find((c) => c.carrierId === props.defaultCarrierId) ?? props.carriers[0])
      : props.carriers[0],
  );
  const [availableTypes, setAvailableTypes] = useState<ZipZoneType[]>(zonesForCarrier(carrier));
  const [type, setType] = useState<ZipZoneType>(
    props.defaultZoneKeyType
      ? availableTypes.find((t) => t.carrierEnum === props.defaultZoneKeyType)!
      : availableTypes[0],
  );
  const [zipZoneString, setZipZoneString] = useState<string>('');
  const [previewZipZoneData, setPreviewZipZoneData] = useState<ZipZoneRecord[]>([]);
  const [showAllRows, setShowAllRows] = useState<boolean>(false);

  const previewTableColumns = generateZipZonePreviewTableColumns(type, (row: ZipZoneRecord) => {
    let isValid = true;
    const countryList = Object.keys(countries.getAlpha2Codes());

    switch (type) {
      case ZipZoneTypes.COUNTRY_TO_COUNTRY:
        isValid = countryList.indexOf(row.fromCountryCode) >= 0 && countryList.indexOf(row.toCountryCode) >= 0;
        break;
      case ZipZoneTypes.COUNTRY_TO_POSTAL5:
        isValid =
          countryList.indexOf(row.fromCountryCode) >= 0 &&
          countryList.indexOf(row.toCountryCode) >= 0 &&
          row.toPostalCode !== '';
        break;
      case ZipZoneTypes.TYPE3_TO_TYPE5:
      case ZipZoneTypes.TYPE5_TO_TYPE5:
        isValid =
          countryList.indexOf(row.fromCountryCode) >= 0 &&
          row.fromPostalCode !== '' &&
          countryList.indexOf(row.toCountryCode) >= 0 &&
          row.toPostalCode !== '';
        break;
    }
    isValid = isValid && (action === ZipZoneAction.DELETE || row.zone !== '');

    return <BooleanShipiumTag value={isValid} />;
  });

  const filteredZipZoneData = previewZipZoneData.filter((zipZone) => {
    const { fromPostalCode, fromCountryCode, toPostalCode, toCountryCode, zone } = zipZone;

    const lcSearchCriteria = searchCriteria?.toLowerCase() || '';
    const lcFromPostalCode =
      ((type === ZipZoneTypes.TYPE3_TO_TYPE5 || type === ZipZoneTypes.TYPE3_TO_TYPE5) &&
        fromPostalCode?.toLowerCase()) ||
      '';
    const lcFromCountryCode = fromCountryCode?.toLowerCase() || '';
    const lcToPostalCode = (type !== ZipZoneTypes.COUNTRY_TO_COUNTRY && toPostalCode?.toLowerCase()) || '';
    const lcToCountryCode = toCountryCode?.toLowerCase() || '';
    const lcZone = zone?.toLowerCase() || '';

    return (
      lcSearchCriteria === '' ||
      lcZone.includes(lcSearchCriteria) ||
      lcFromPostalCode.includes(lcSearchCriteria) ||
      lcFromCountryCode.includes(lcSearchCriteria) ||
      lcToPostalCode.includes(lcSearchCriteria) ||
      lcToCountryCode.includes(lcSearchCriteria)
    );
  });

  const onSearch = () => {
    const { searchCriteria } = form.getFieldsValue();
    setSearchCriteria(searchCriteria);
  };

  const handleKeyDown = (event: any) => {
    // Enter
    if (event?.keyCode === 13) {
      onSearch();
    }

    // Escape
    if (event?.keyCode === 27) {
      form.setFieldValue('searchCriteria', '');
      setSearchCriteria('');
      event?.stopPropagation();
    }
  };

  const generateTemplateCsv = async () => {
    const matrix: string[][] = generateZipZoneTemplateMatrix(type);
    const csvString = await convertMatrixToCsvString(matrix);
    const now: Date = new Date();
    downloadCsvFile(
      csvString,
      `${carrier.carrierId}-zipzone-${action.toLowerCase()}-${type.carrierEnum.replaceAll('_', '-')}-${format(now, 'yyyy-MM-dd')}`,
    );
  };

  return (
    <>
      <Upload
        accept=".csv"
        beforeUpload={() => false}
        multiple={false}
        showUploadList={false}
        onChange={async (val) => {
          const csvString = await readFileAsString(val.file as any);
          setZipZoneString(csvString);
          const csvMatrix = convertCsvToMatrix(csvString);

          // TODO: Add error handling if incoming CSV doesn't match type
          // TODO: Consider checking if the first row contains headers or not.
          const zipZoneData = extractZipZoneDataFromMatrix(csvMatrix);

          setPreviewZipZoneData(zipZoneData);
          // We use the zipZoneData[0]?.fromCountryCode as the originCountryCode because it most accurately represents the behavior of the API.
          // The API picks the first origin country code from the zipzone upload and uses that for all the origins,
          // regardless of whether the upload had more than one origin country code in the file.
          props.setZipZones({
            type: type,
            csvString: csvString,
            action: action,
            carrier: carrier,
            originCountryCode: zipZoneData[0]?.fromCountryCode,
          });
        }}
      >
        <Button>
          <UploadOutlined /> Upload Zip Zones CSV
        </Button>
      </Upload>
      <Button
        onClick={async () => {
          await generateTemplateCsv();
        }}
      >
        <DownloadOutlined /> Download CSV Template
      </Button>
      <Select
        value={action}
        dropdownMatchSelectWidth={false}
        onChange={(val: ZipZoneAction) => {
          setAction(val);
          props.setZipZones({
            type: type,
            csvString: zipZoneString,
            action: val,
            carrier: carrier,
            originCountryCode: previewZipZoneData[0]?.fromCountryCode,
          });
        }}
      >
        <Select.Option key={ZipZoneAction.ADD} value={ZipZoneAction.ADD}>
          <Tooltip
            placement="left"
            title="Add the supplied zone mappings non-destructively. If there is an existing zone and a new zone for a key, that key will be associated with both zones."
          >
            {ZipZoneAction.ADD}
          </Tooltip>
        </Select.Option>
        <Select.Option key={ZipZoneAction.DELETE} value={ZipZoneAction.DELETE}>
          <Tooltip placement="left" title="Delete the supplied zone mappings.">
            {ZipZoneAction.DELETE}
          </Tooltip>
        </Select.Option>
        <Select.Option key={ZipZoneAction.REPLACE} value={ZipZoneAction.REPLACE}>
          <Tooltip
            placement="left"
            title="Replace any existing zone mappings with the supplied origin (e.g. country + postal code) with the new zone mappings. Any zone mappings for the supplied origin without a new zone will be deleted."
          >
            {ZipZoneAction.REPLACE}
          </Tooltip>
        </Select.Option>
      </Select>
      <Select
        showSearch
        value={carrier.carrierId}
        dropdownMatchSelectWidth={false}
        onChange={(val: string) => {
          const selectedCarrier = props.carriers.find((c) => c.carrierId === val);
          if (selectedCarrier) {
            setCarrier(selectedCarrier);
            const zoneTypes = zonesForCarrier(selectedCarrier);
            if (zoneTypes.length > 0) {
              setAvailableTypes(zoneTypes);
              if (zoneTypes.indexOf(type) < 0) {
                setType(zoneTypes[0]);
                props.setZipZones({
                  type: zoneTypes[0],
                  csvString: zipZoneString,
                  action: action,
                  carrier: selectedCarrier,
                  originCountryCode: previewZipZoneData[0]?.fromCountryCode,
                });
              } else {
                props.setZipZones({
                  type: type,
                  csvString: zipZoneString,
                  action: action,
                  carrier: selectedCarrier,
                  originCountryCode: previewZipZoneData[0]?.fromCountryCode,
                });
              }
            } else {
              setAvailableTypes(Object.values(ZipZoneTypes));
              props.setZipZones({
                type: type,
                csvString: zipZoneString,
                action: action,
                carrier: selectedCarrier,
                originCountryCode: previewZipZoneData[0]?.fromCountryCode,
              });
            }
          }
        }}
      >
        {props.carriers.map((c) => (
          <Select.Option key={c.name} value={c.carrierId}>
            {c.name}
          </Select.Option>
        ))}
      </Select>
      <Select
        value={type.displayName}
        dropdownMatchSelectWidth={false}
        onChange={(val: string) => {
          const selectedZoneType = availableTypes.find((t) => t.displayName === val);
          if (selectedZoneType) {
            setType(selectedZoneType);
            props.setZipZones({
              type: selectedZoneType,
              csvString: zipZoneString,
              action: action,
              carrier: carrier,
              originCountryCode: previewZipZoneData[0]?.fromCountryCode,
            });
          }
        }}
      >
        {availableTypes.map((t) => (
          <Select.Option key={t.displayName} value={t.displayName}>
            {t.displayName}
          </Select.Option>
        ))}
      </Select>
      {previewZipZoneData?.length > 0 && (
        <>
          <Form
            form={form}
            layout={'horizontal'}
            style={{ marginTop: '1rem' }}
            onKeyDown={handleKeyDown}
            autoFocus={true}
          >
            <Row>
              <Space>
                <Form.Item name="searchCriteria">
                  <Input allowClear={true} placeholder="Enter Search Criteria" />
                </Form.Item>
                <Form.Item>
                  <Button type={'primary'} onClick={onSearch}>
                    Search
                  </Button>
                </Form.Item>
              </Space>
            </Row>
          </Form>
          <Table
            pagination={false}
            dataSource={showAllRows ? filteredZipZoneData : filteredZipZoneData.slice(0, DEFAULT_ROWS_TO_SHOW)}
            columns={previewTableColumns}
          />
          {filteredZipZoneData.length > DEFAULT_ROWS_TO_SHOW && (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <Button onClick={() => setShowAllRows(!showAllRows)}>
                {!showAllRows ? (
                  <>
                    Show Additional {filteredZipZoneData.length - DEFAULT_ROWS_TO_SHOW} rows <EyeOutlined />
                  </>
                ) : (
                  <>
                    Show Less <EyeOutlined />
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ZipZoneUpload;
